import { isEmpty } from 'lodash'

import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, post } from '../request'

const fetchProgramEnrolments = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.programs}/enrolments`, queries)

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching program enrolments')
    throw error
  }
}

const getExplorationStandards = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'EXPLORATION_STANDARDS'
  })

  try {
    const response = await get(requestUrl)

    // Defensive: local / sparse DBs may not have this AppConfig row.
    // Returning an empty list lets callers render their empty state
    // instead of throwing "Cannot read properties of undefined".
    return response?.data?.results?.[0]?.value ?? []
  } catch (error) {
    sentryLogger(error, 'error in fetching exploration standards')
    throw error
  }
}

const getProgramsList = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.programs}/programs`)

  try {
    const response = await get(requestUrl)

    return response.data.results
  } catch (error) {
    sentryLogger(error, 'error in fetching programs list')
    throw error
  }
}

const getProgramCost = async ({ queries }) => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.programs}/cost`, queries)

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching programs cost')
    throw error
  }
}

const terminateProgramEnrolment = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.programs}/enrolment/terminate`,
      {
        end_date: data?.endDate,
        program_enrolment_uuid: data.enrolment_uuid,
        termination_reason: data.reason,
        termination_notes: data.notes
      }
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in terminate enrolment')
    throw error
  }
}

const getProgramEnrolmentPauseEligibility = async (data) => {
  try {
    const url = `${apiEndpoints.programs}/enrolment/${data.enrolment_uuid}/pause_eligibility`
    const response = await get(url)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching pause eligibility')
    throw error
  }
}

const pauseProgramEnrolment = async (data) => {
  try {
    const response = await post(`${apiEndpoints.programs}/enrolment/pause`, {
      program_enrolment: data.enrolment_uuid,
      start_date: data.start_date,
      end_date: data.end_date,
      reason: data.reason
    })

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in pause enrolment')
    throw error
  }
}

const fetchProgramCapacity = async (data) => {
  try {
    const requestUrl = makeRequestUrl(
      `${apiEndpoints.programs}/enrolment/capacity`,
      data
    )
    const response = await get(requestUrl)
    return response.data.results[0]
  } catch (error) {
    sentryLogger(error, 'error in fetching program capacity')
    throw error
  }
}

// --- Excel program-enrolment actions (program-native pause + change batch) ---

const getExcelPauseEligibility = async (programEnrolmentUuid) => {
  try {
    const response = await get(
      `${apiEndpoints.programs}/enrolment/${programEnrolmentUuid}/excel/pause_eligibility`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching excel pause eligibility')
    throw error
  }
}

const pauseExcelEnrolment = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.programs}/enrolment/excel/pause`,
      {
        program_enrolment: data.program_enrolment,
        month: data.month,
        year: data.year
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in pausing excel enrolment')
    throw error
  }
}

const getExcelChangeBatchEligibility = async (programEnrolmentUuid) => {
  try {
    const response = await get(
      `${apiEndpoints.programs}/enrolment/${programEnrolmentUuid}/excel/change_batch_eligibility`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching excel batch change eligibility')
    throw error
  }
}

const changeExcelBatch = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.programs}/enrolment/excel/change_batch`,
      {
        program_enrolment: data.program_enrolment,
        new_batch: data.new_batch
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in changing excel batch')
    throw error
  }
}

// Preschool teacher remap — batch roster + destination resolution. Called
// with no teacher for the roster, then re-called as the admin picks a teacher
// / date / selection so the preview numbers track what is actually ticked.
const getPreschoolTeacherRemapEligibility = async ({
  batchUuid,
  newTeacher,
  effectiveDate,
  programEnrolments,
  targetBatch
} = {}) => {
  try {
    const queries = {}
    if (newTeacher) queries.new_teacher = newTeacher
    if (targetBatch) queries.target_batch = targetBatch
    if (effectiveDate) queries.effective_date = effectiveDate
    if (programEnrolments?.length) {
      queries.program_enrolments = programEnrolments.join(',')
    }
    const response = await get(
      makeRequestUrl(
        `${apiEndpoints.programs}/batch/${batchUuid}/teacher_remap_eligibility`,
        queries
      )
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching teacher remap eligibility')
    throw error
  }
}

// Applies the remap. Resolves with a per-child result list — children commit
// independently, so a partial success is a real outcome the caller must render
// rather than treat as failure.
const remapPreschoolStudents = async ({
  batchUuid,
  newTeacher,
  programEnrolments,
  effectiveDate,
  targetBatch
}) => {
  try {
    const response = await post(
      `${apiEndpoints.programs}/batch/teacher_remap`,
      {
        batch: batchUuid,
        new_teacher: newTeacher,
        target_batch: targetBatch,
        program_enrolments: programEnrolments,
        effective_date: effectiveDate,
        confirm: true
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in remapping preschool students')
    throw error
  }
}

const getExcelResumeEligibility = async (programEnrolmentUuid, resumeDate) => {
  try {
    const response = await get(
      `${apiEndpoints.programs}/enrolment/${programEnrolmentUuid}/excel/resume_eligibility?resume_date=${resumeDate}`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching excel resume eligibility')
    throw error
  }
}

const resumeExcelEnrolment = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.programs}/enrolment/excel/resume`,
      {
        program_enrolment: data.program_enrolment,
        resume_date: data.resume_date
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in resuming excel enrolment')
    throw error
  }
}

const getImmediateTerminationPreview = async ({ enrolment_uuid }) => {
  try {
    const response = await get(
      `${apiEndpoints.programs}/enrolment/${enrolment_uuid}/terminate_immediate_preview`
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching immediate termination preview')
    throw error
  }
}

const terminateProgramEnrolmentImmediately = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.programs}/enrolment/terminate_immediate`,
      {
        program_enrolment_uuid: data.enrolment_uuid,
        termination_reason: data.reason,
        termination_notes: data.notes,
        // The backend refuses an unconfirmed immediate termination outright.
        confirm: true
      }
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in immediate terminate enrolment')
    throw error
  }
}

export {
  fetchProgramCapacity,
  fetchProgramEnrolments,
  getExplorationStandards,
  getProgramCost,
  getProgramsList,
  terminateProgramEnrolment,
  terminateProgramEnrolmentImmediately,
  getImmediateTerminationPreview,
  pauseProgramEnrolment,
  getProgramEnrolmentPauseEligibility,
  getExcelPauseEligibility,
  pauseExcelEnrolment,
  getExcelChangeBatchEligibility,
  changeExcelBatch,
  getExcelResumeEligibility,
  resumeExcelEnrolment,
  getPreschoolTeacherRemapEligibility,
  remapPreschoolStudents
}
